import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Spinner, Alert } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const EditOrder = () => {
  const [formData, setFormData] = useState({
    customer_id: "",
    date: new Date().toISOString().split('T')[0],
    productSearch: "",
    selling_rate: "",
    quantity: "",
    gst_percentage: 18,
    status: "Pending"
  });

  const [orderItems, setOrderItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    address: "",
    gstNo: ""
  });
  
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingProducts, setFetchingProducts] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Customer modal states
  const [customerErrors, setCustomerErrors] = useState({});
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [customerSuccess, setCustomerSuccess] = useState("");

  // Refs for input fields
  const customerRef = useRef(null);
  const dateRef = useRef(null);
  const productSearchRef = useRef(null);
  const sellingRateRef = useRef(null);
  const qtyRef = useRef(null);

  const navigate = useNavigate();
  const { id } = useParams();

  // Fetch all data
  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchCustomers(),
        fetchProducts(),
        fetchOrderData()
      ]);
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setSubmitError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.post("/customer_list.php", {});
      if (response.data.head.code === 200) {
        setCustomers(response.data.body);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      setFetchingProducts(true);
      const response = await api.post("/product_list.php", {});
      
      if (response.data.head.code === 200) {
        setProducts(response.data.body);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setFetchingProducts(false);
    }
  };
 const formatDate = (date) => {
          console.log("API date:", date);
          if (!date) return "";

          // date = "18-12-2025"
          const [day, month, year] = date.split("-");

          return `${year}-${month}-${day}`; // "2025-12-18"
        };
  const fetchOrderData = async () => {
    try {
      const response = await api.post("/order_detail.php", { id });
      
      if (response.data.head.code === 200) {
        const data = response.data.body;
        setFormData({
          customer_id: data.customer_id || "",
          // date: data.order_date || new Date().toISOString().split('T')[0],
          date:formatDate(data.order_date),
          productSearch: "",
          selling_rate: "",
          quantity: "",
          gst_percentage: data.gst_percentage || 18,
          status: data.status || "Pending"
        });

        // Transform items
        const items = data.items.map(item => ({
          id: item.id || Date.now(),
          product_id: item.product_id,
          product_code: item.product_code,
          item_name: item.item_name,
          selling_rate: parseFloat(item.selling_rate) || 0,
          quantity: parseInt(item.quantity) || 1,
          total: parseFloat(item.total) || 0
        }));
        
        setOrderItems(items);
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setSubmitError("Failed to load order data");
    }
  };

  // Filter products based on search input
  const getFilteredProducts = () => {
    if (!formData.productSearch) {
      return [];
    }
    
    const searchTerm = formData.productSearch.toLowerCase();
    return products.filter(product => {
      const productCode = product.product_code || product.code || product.product_code || "";
      const productName = product.name || product.product_name || product.item_name || "";
      const category = product.category || "";
      
      return (
        productCode.toLowerCase().includes(searchTerm) ||
        productName.toLowerCase().includes(searchTerm) ||
        category.toLowerCase().includes(searchTerm)
      );
    }).slice(0, 10);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'productSearch') {
      setShowProductDropdown(true);
    }
  };

  // Handle product selection from dropdown
  const handleProductSelect = (product) => {
    // const productCode = product.product_code || product.code || "";
    const productName = product.name || product.product_name || product.item_name || "";
    const hsnCode = product.hsn_code;
    const productId = product.id; // assume this comes from your product object

    const productCode = `PROD${productId.toString().length === 1
        ? "0" + productId
        : productId
      }`;
    
    setFormData(prev => ({
      ...prev,
      productSearch: `${productCode} - ${productName}`,
      selectedProduct: {
        product_code: productCode,
        name: productName
      }
    }));
    setShowProductDropdown(false);
    sellingRateRef.current.focus();
  };

  const handleAddItem = () => {
    if (!formData.selectedProduct || !formData.selling_rate || !formData.quantity) {
      alert("Please fill all product fields");
      return;
    }

    const newItem = {
      id: Date.now(),
      product_id: formData.selectedProduct.id,
      product_code: formData.selectedProduct.product_code,
      item_name: formData.selectedProduct.name,
      selling_rate: parseFloat(formData.selling_rate) || 0,
      quantity: parseInt(formData.quantity) || 1,
      total: (parseFloat(formData.selling_rate) || 0) * (parseInt(formData.quantity) || 1)
    };

    setOrderItems([...orderItems, newItem]);
    
    // Reset product fields only
    setFormData(prev => ({ 
      ...prev, 
      productSearch: "",
      selectedProduct: null,
      selling_rate: "",
      quantity: ""
    }));
    
    // Focus back on product search for next entry
    productSearchRef.current.focus();
    setShowProductDropdown(false);
  };

  // Enter key navigation handler
  const handleKeyPress = (e, nextFieldRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextFieldRef && nextFieldRef.current) {
        nextFieldRef.current.focus();
      }
    }
  };

  // Special handler for product search
  const handleProductSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filtered = getFilteredProducts();
      if (filtered.length === 1) {
        handleProductSelect(filtered[0]);
      } else if (filtered.length > 0 && formData.productSearch) {
        sellingRateRef.current.focus();
      }
    }
  };

  // Special handler for selling rate field
  const handleSellingRateKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (formData.selectedProduct && formData.selling_rate) {
        qtyRef.current.focus();
      }
    }
  };

  // Special handler for quantity field to add item
  const handleQtyKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleGstChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, gst_percentage: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...orderItems];
    
    if (field === 'selling_rate') {
      updatedItems[index].selling_rate = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      updatedItems[index].quantity = parseInt(value) || 0;
    } else {
      updatedItems[index][field] = value;
    }
    
    // Calculate total if selling rate or quantity changes
    if (field === 'selling_rate' || field === 'quantity') {
      updatedItems[index].total = updatedItems[index].selling_rate * updatedItems[index].quantity;
    }
    
    setOrderItems(updatedItems);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(updatedItems);
  };

  // Customer Modal Functions
  const handleShowCustomerModal = () => {
    setShowCustomerModal(true);
    setCustomerErrors({});
    setCustomerSuccess("");
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    // Reset form
    setNewCustomer({
      name: "",
      phone: "",
      address: "",
      gstNo: "",
      state: "",
      city: "",
      pin_code: ""
    });
    setCustomerErrors({});
    setCustomerSuccess("");
    setIsAddingCustomer(false);
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (customerErrors[name]) {
      setCustomerErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Validate customer form
  const validateCustomer = () => {
    const errors = {};
    
    if (!newCustomer.name.trim()) {
      errors.name = "Customer name is required";
    }
    
    if (!newCustomer.phone.trim()) {
      errors.phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(newCustomer.phone)) {
      errors.phone = "Enter a valid 10-digit mobile number";
    }
    
    return errors;
  };

  // Handle adding customer via API
  const handleAddCustomer = async () => {
    const validationErrors = validateCustomer();
    
    if (Object.keys(validationErrors).length > 0) {
      setCustomerErrors(validationErrors);
      return;
    }

    try {
      setIsAddingCustomer(true);
      setCustomerSuccess("");
      setCustomerErrors({});

      // Prepare payload matching customer_add.php API
      const payload = {
        name: newCustomer.name,
        mobile: newCustomer.phone,
        gst_no: newCustomer.gstNo || "",
        state: newCustomer.state || "",
        city: newCustomer.city || "",
        address: newCustomer.address || "",
        pin_code: newCustomer.pin_code || ""
      };

      // Call the customer_add.php API
      const response = await api.post("/customer_add.php", payload);

      if (response.data.head.code === 200) {
        // Success - add new customer to local state
        const newCustomerObj = {
          id: response.data.body.customer_id,
          name: newCustomer.name,
          mobile: newCustomer.phone,
          gst_no: newCustomer.gstNo,
          state: newCustomer.state,
          city: newCustomer.city,
          address: newCustomer.address,
          pin_code: newCustomer.pin_code
        };

        // Add to customers list
        setCustomers(prev => [...prev, newCustomerObj]);
        
        // Auto-select the new customer
        setFormData(prev => ({ 
          ...prev, 
          customer_id: response.data.body.customer_id 
        }));

        // Show success message
        setCustomerSuccess(`Customer "${newCustomer.name}" added successfully!`);
        
        // Reset form and close modal after 2 seconds
        setTimeout(() => {
          handleCloseCustomerModal();
          // Focus back on customer dropdown
          if (customerRef.current) {
            customerRef.current.focus();
          }
        }, 2000);

      } else {
        // Handle API error
        const errorMsg = response.data.head.msg || "Failed to add customer";
        setCustomerErrors({ api: errorMsg });
      }
    } catch (err) {
      console.error("Error adding customer:", err);
      
      let errorMessage = "Failed to add customer. Please try again.";
      
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error("Response error:", err.response.data);
        errorMessage = err.response.data?.head?.msg || errorMessage;
      } else if (err.request) {
        // The request was made but no response was received
        console.error("No response received:", err.request);
        errorMessage = "No response from server. Please check your connection.";
      }
      
      setCustomerErrors({ api: errorMessage });
    } finally {
      setIsAddingCustomer(false);
    }
  };

  // Handle keydown in customer modal
  const handleCustomerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddCustomer();
    }
  };

  const calculateSubTotal = () => {
    return orderItems.reduce((total, item) => total + (item.total || 0), 0);
  };

  const calculateGST = () => {
    const subTotal = calculateSubTotal();
    return (subTotal * (parseFloat(formData.gst_percentage) / 100));
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const gst = calculateGST();
    return subTotal + gst;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_id) newErrors.customer_id = "Customer is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (orderItems.length === 0) newErrors.items = "At least one item is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setSaving(true);
      setSubmitError("");
      
      const payload = {
        id: id,
        customer_id: formData.customer_id,
        order_date: formData.date,
        sub_total: calculateSubTotal().toFixed(2),
        gst_percentage: formData.gst_percentage,
        gst_amount: calculateGST().toFixed(2),
        grand_total: calculateGrandTotal().toFixed(2),
        status: formData.status,
        items: orderItems.map(item => ({
          product_id: item.product_id,
          product_code: item.product_code,
          item_name: item.item_name,
          selling_rate: item.selling_rate.toFixed(2),
          quantity: item.quantity,
          total: item.total.toFixed(2)
        }))
      };
      console.log("payload is ",payload);
/*  */
      const response = await api.post("/order_edit.php", payload);
      
      if (response.data.head.code === 200) {
        alert("Order updated successfully!");
        navigate("/orderlist");
      } else {
        const errorMsg = response.data.head.msg || "Failed to update order";
        setSubmitError(errorMsg);
      }
     
    } catch (err) {
      console.error("Error updating order:", err);
      setSubmitError("Failed to update order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderError = (field) =>
    errors[field] ? <div className="text-danger small mt-1">{errors[field]}</div> : null;

  // Render customer field error
  const renderCustomerError = (field) =>
    customerErrors[field] ? (
      <div className="text-danger small mt-1">{customerErrors[field]}</div>
    ) : null;

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading order data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Edit Order</h4>
        <Button className="btn-top" onClick={() => navigate(-1)}>Back</Button>
      </div>

      {submitError && (
        <Alert variant="danger" className="mb-3">
          {submitError}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Select Customer *</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  ref={customerRef}
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, dateRef)}
                  isInvalid={!!errors.customer_id}
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </Form.Select>
                <Button 
                  variant="outline-primary" 
                  onClick={handleShowCustomerModal}
                  className="d-flex align-items-center"
                  title="Add New Customer"
                  type="button"
                >
                  <FaPlus />
                </Button>
              </div>
              {renderError("customer_id")}
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Date *</Form.Label>
              <Form.Control
                ref={dateRef}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                onKeyPress={(e) => handleKeyPress(e, productSearchRef)}
                isInvalid={!!errors.date}
              />
              {renderError("date")}
            </Form.Group>
          </Col>

          {/* Status Field */}
          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Shipped">Shipped</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Product Search Row */}
          <Col lg={4} md={4} sm={12}>
            <Form.Group className="position-relative">
              <Form.Label>Search Product *</Form.Label>
              <Form.Control
                ref={productSearchRef}
                name="productSearch"
                type="text"
                value={formData.productSearch}
                onChange={handleChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                onKeyPress={handleProductSearchKeyPress}
                placeholder="Type product code or name..."
                autoComplete="off"
                disabled={fetchingProducts}
              />
              {fetchingProducts && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {showProductDropdown && (
                <div className="custom-dropdown">
                  {getFilteredProducts().length > 0 ? (
                    getFilteredProducts().map((product, index) => {
                      // const productCode = product.product_code || product.code || "";
                      const productName = product.name || product.product_name || product.item_name || "";
                      const category = product.category || "";
                       const productId = product.id; // assume this comes from your product object

const productCode = `PROD${
  productId.toString().length === 1
    ? "0" + productId
    : productId
}`;
                      
                      return (
                        <div
                          key={index}
                          className="dropdown-item"
                          onClick={() => handleProductSelect(product)}
                        >
                          <div className="fw-bold">{productCode} - {productName}</div>
                          {category && (
                            <div className="small text-muted">{category}</div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="dropdown-item text-muted">
                      {formData.productSearch ? "No products found" : "Start typing to search products"}
                    </div>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={6}>
            <Form.Group>
              <Form.Label>Selling Rate (₹) *</Form.Label>
              <Form.Control
                ref={sellingRateRef}
                className="right-align"
                name="selling_rate"
                type="number"
                value={formData.selling_rate}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
                onKeyPress={handleSellingRateKeyPress}
              />
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={6}>
            <Form.Group>
              <Form.Label>Quantity *</Form.Label>
              <Form.Control
                ref={qtyRef}
                className="right-align"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="0"
                min="1"
                onKeyPress={handleQtyKeyPress}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Order Items Table */}
        <Row className="mt-4">
          <Col xs={12}>
            <div className="table-responsive">
              <Table bordered className="purchase-table">
                <thead className="table-dark">
                  <tr>
                    <th width="5%">S.No</th>
                    
                    <th width="25%">Item</th>
                    {/* <th width="20%">HSN Code</th> */}
                    <th width="15%">Selling Rate (₹)</th>
                    <th width="10%">Qty</th>
                    <th width="15%">Total (₹)</th>
                    <th width="10%">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item, index) => (
                    <tr key={item.id}>
                      <td className="text-center">{index + 1}</td>
                      
                      <td>{item.item_name}</td>
                      {/* <td>{item.hsn_code}</td> */}
                      <td>
                        <Form.Control
                          type="number"
                          value={item.selling_rate}
                          onChange={(e) => handleItemChange(index, 'selling_rate', e.target.value)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          className="right-align"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="0"
                          min="1"
                          className="right-align"
                        />
                      </td>
                      <td className="text-center">₹{item.total.toFixed(2)}</td>
                      <td className="text-center">
                        <FaTrash
                          className="delete-icon"
                          style={{ cursor: 'pointer', color: '#dc3545' }}
                          onClick={() => handleDeleteItem(index)}
                          title="Delete Item"
                        />
                      </td>
                    </tr>
                  ))}
                  {orderItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        No items added yet. Search products and press Enter in quantity field to add items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {renderError("items")}

        {/* Totals Section */}
        <Row className="mt-4">
          <Col xs={12} className="d-flex justify-content-end">
            <div className="totals-section">
              <div className="total-row">
                <span className="total-label">Subtotal:</span>
                <span className="total-value">₹{calculateSubTotal().toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span className="total-label">
                  GST (
                  <Form.Control
                    type="number"
                    value={formData.gst_percentage}
                    onChange={handleGstChange}
                    className="gst-input"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  %)
                </span>
                <span className="total-value">₹{calculateGST().toFixed(2)}</span>
              </div>
              <div className="total-row grand-total">
                <span className="total-label">Grand Total:</span>
                <span className="total-value">₹{calculateGrandTotal().toFixed(2)}</span>
              </div>
            </div>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button type="submit" className="btn-submit" disabled={saving || orderItems.length === 0}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : "Update Order"}
          </Button>
          <Button type="button" className="btn-top" onClick={() => navigate(-1)} disabled={saving}>
            Cancel
          </Button>
        </div>
      </Form>

      {/* Add Customer Modal */}
      <Modal show={showCustomerModal} onHide={handleCloseCustomerModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customerSuccess && (
            <Alert variant="success" className="mb-3">
              {customerSuccess}
            </Alert>
          )}

          {customerErrors.api && (
            <Alert variant="danger" className="mb-3">
              {customerErrors.api}
            </Alert>
          )}

          <Form onKeyDown={handleCustomerKeyDown}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newCustomer.name}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter customer name"
                    required
                    isInvalid={!!customerErrors.name}
                    autoFocus
                  />
                  {renderCustomerError("name")}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile *</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={newCustomer.phone}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    isInvalid={!!customerErrors.phone}
                  />
                  {renderCustomerError("phone")}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={newCustomer.address}
                onChange={handleNewCustomerChange}
                placeholder="Enter address"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>GST Number (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="gstNo"
                value={newCustomer.gstNo}
                onChange={handleNewCustomerChange}
                placeholder="Enter GST number"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleCloseCustomerModal}
            disabled={isAddingCustomer}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddCustomer} 
            disabled={!newCustomer.name.trim() || !newCustomer.phone.trim() || isAddingCustomer}
          >
            {isAddingCustomer ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Customer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EditOrder;